import React, { useRef } from "react";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import styles from "./ConsultOrderPromptModal.module.scss";

// ITAM-2583 + ITAM-2614: the two-choice step shown before opening either form
// (regular order vs RFZO therapy). The chip ALWAYS reopens this prompt — no
// mode is ever entered silently — and Escape/overlay/close simply decline
// (onClose): drafts and results are preserved behind the chip, nothing is
// discarded. Labels adapt to what already exists for this consult:
//   - order action:  "Rezervacija" / "Nastavi rezervaciju"; DISABLED (with a
//     note) while an orderable RFZO therapy exists — the therapy is already
//     persisted, so the RFZO order path is the only way forward (invariant 4);
//   - RFZO action:   "RFZO terapija" / "Nastavi RFZO terapiju" (draft),
//     "Nastavi RFZO rezervaciju" (orderable result -> resume), or
//     "Pregled RFZO terapije" (terminal result -> view-only summary).

export type PromptRfzoState = "NONE" | "ORDERABLE" | "TERMINAL";

export interface ConsultOrderPromptModalProps {
  isOpen: boolean;
  callerName: string;
  cncFormVisited: boolean;
  rfzoFormVisited: boolean;
  rfzoState: PromptRfzoState;
  onConfirmOrder: () => void;
  onConfirmRfzo: () => void;
  onResumeRfzo: () => void;
  onClose: () => void;
}

const TITLE_ID = "consult-order-prompt-title";
const ORDER_DISABLED_NOTE_ID = "consult-order-prompt-order-note";

const ConsultOrderPromptModal: React.FC<ConsultOrderPromptModalProps> = ({
  isOpen,
  callerName,
  cncFormVisited,
  rfzoFormVisited,
  rfzoState,
  onConfirmOrder,
  onConfirmRfzo,
  onResumeRfzo,
  onClose,
}) => {
  const orderRef = useRef<HTMLButtonElement>(null);
  const rfzoRef = useRef<HTMLButtonElement>(null);

  const orderDisabled = rfzoState === "ORDERABLE";
  const orderLabel = cncFormVisited ? "Nastavi rezervaciju" : "Rezervacija";

  const rfzoLabel =
    rfzoState === "ORDERABLE"
      ? "Nastavi RFZO rezervaciju"
      : rfzoState === "TERMINAL"
      ? "Pregled RFZO terapije"
      : rfzoFormVisited
      ? "Nastavi RFZO terapiju"
      : "RFZO terapija";
  const rfzoAction = rfzoState === "NONE" ? onConfirmRfzo : onResumeRfzo;

  return (
    <AccessibleDialog
      isOpen={isOpen}
      onRequestClose={onClose}
      titleId={TITLE_ID}
      initialFocusRef={orderDisabled ? rfzoRef : orderRef}
      maxWidth={440}
      closeButtonLabel="Zatvori"
    >
      <div className={styles.body}>
        <h2 id={TITLE_ID} className={styles.title}>
          Kreiranje za korisnika
        </h2>
        <p className={styles.message}>
          Šta želite da kreirate za {callerName}?
        </p>
        {orderDisabled && (
          <p
            id={ORDER_DISABLED_NOTE_ID}
            className={styles.orderDisabledNote}
            role="note"
          >
            RFZO terapija je već kreirana za ovog korisnika — nastavite RFZO
            rezervaciju.
          </p>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondary}
            onClick={onClose}
          >
            Otkaži
          </button>
          <button
            type="button"
            ref={rfzoRef}
            className={styles.rfzo}
            onClick={rfzoAction}
          >
            {rfzoLabel}
          </button>
          <button
            type="button"
            ref={orderRef}
            className={styles.primary}
            onClick={onConfirmOrder}
            disabled={orderDisabled}
            aria-describedby={
              orderDisabled ? ORDER_DISABLED_NOTE_ID : undefined
            }
          >
            {orderLabel}
          </button>
        </div>
      </div>
    </AccessibleDialog>
  );
};

export default ConsultOrderPromptModal;
